/** Object/array data field: shows the pretty-printed value by default; click to
 *  expand into the JSON editor (JsonEditorWidget, code mode). "Done" validates
 *  and commits; invalid JSON keeps the editor open with an error. */
import { Alert, Box, Button, Stack } from '@mui/material';
import { useMemo, useRef, useState } from 'react';

import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';

interface Props {
  value: unknown;
  onChange: (next: unknown) => void;
  readOnly?: boolean;
}

export function JsonValueField({ value, onChange, readOnly = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<JsonEditorHandle>(null);

  const pretty = useMemo(() => {
    try {
      return JSON.stringify(value ?? null, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  if (!expanded) {
    return (
      <Box
        onClick={() => setExpanded(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(true); }}
        sx={{
          cursor: 'pointer', border: 1, borderColor: 'divider', borderRadius: 1,
          px: 1, py: 0.5, maxHeight: 160, overflow: 'auto', bgcolor: 'action.hover',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {pretty || '—'}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ height: 280, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <JsonEditorWidget
          ref={ref}
          initialValue={(value ?? {}) as Record<string, unknown>}
          mode="code"
          readOnly={readOnly}
        />
      </Box>
      {err && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErr(null)}>{err}</Alert>}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {!readOnly && (
          <Button
            size="small" variant="outlined"
            onClick={() => {
              try {
                onChange(ref.current?.getValue() as unknown);
                setErr(null);
                setExpanded(false);
              } catch (e) {
                setErr(e instanceof Error ? e.message : 'Invalid JSON');
              }
            }}
          >
            Done
          </Button>
        )}
        <Button size="small" onClick={() => { setErr(null); setExpanded(false); }}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </Stack>
    </Box>
  );
}
